import { useCallback, useMemo, useState } from 'react';

/**
 * Shared edit-state for the CLW review grid — one implementation of the
 * editedRows / pending-list-ops / editing-cell reducer that previously lived
 * as three drifting copies in CampaignLaunchWorkflow, LaunchCampaignDetails
 * and CopyPlanStep.
 *
 * Pending list operations generalize the old pendingDeletions shape so the
 * cell detail panel can stage adds and per-item edits, not just removals:
 *
 *   pendingListOps: {
 *     [rowId]: {
 *       [field]: [ { op: 'remove'|'add'|'edit', value, newValue? } ]
 *     }
 *   }
 *
 * The legacy `pendingDeletions` view ({ rowId: { field: [value] } }, remove
 * ops only) is derived from it so existing consumers — the appliers in
 * reviewEdits/copyReviewEdits and the panel's strikethrough rendering —
 * keep working unchanged while list adds/edits are rolled out.
 *
 * Modes:
 * - 'batched' (CLW review pane, LaunchCampaignDetails): edits accumulate in
 *   state until the host saves them in one PUT.
 * - 'immediate' (copy workflow): nothing accumulates; each commit calls
 *   `onCommitEdit(rowId, field, value)` / `onListOp(rowId, field, op)` so the
 *   host can apply-and-save per commit, preserving the copy flow's
 *   one-save-per-commit contract.
 */

// Legacy adapter: remove ops → the { rowId: { field: [value] } } deletions
// shape the appliers and detail panel currently consume.
export function listOpsToDeletions(listOps) {
  const deletions = {};
  for (const [rowId, fields] of Object.entries(listOps || {})) {
    for (const [field, ops] of Object.entries(fields || {})) {
      const removed = (ops || []).filter((o) => o.op === 'remove').map((o) => o.value);
      if (removed.length === 0) continue;
      if (!deletions[rowId]) deletions[rowId] = {};
      deletions[rowId][field] = removed;
    }
  }
  return deletions;
}

// Immutable upsert helper. `mutate` receives the row's current ops array for
// `field` and returns the next one; empty arrays prune the field/row keys so
// pendingListOps stays {} when nothing is staged (save buttons key off that).
function updateOps(prev, rowId, field, mutate) {
  const next = { ...prev };
  const row = { ...(next[rowId] || {}) };
  const ops = mutate([...(row[field] || [])]);
  if (ops.length === 0) delete row[field];
  else row[field] = ops;
  if (Object.keys(row).length === 0) delete next[rowId];
  else next[rowId] = row;
  return next;
}

export default function useReviewGridEdits({ mode = 'batched', onCommitEdit, onListOp } = {}) {
  const [editedRows, setEditedRows] = useState({}); // rowId → { field: value }
  const [pendingListOps, setPendingListOps] = useState({});
  // Row-level ops — whole line items / ad groups being duplicated, deleted or
  // cloned. A flat LIST rather than a rowId-keyed map because one row can carry
  // several (duplicate it three times) and because order matters to the applier.
  // Only the bulk-edit agent stages these today; the cell editors never do.
  const [pendingRowOps, setPendingRowOps] = useState([]);
  const [editingCell, setEditingCell] = useState(null); // { rowId, field }
  const [editingDraft, setEditingDraft] = useState('');

  const immediate = mode === 'immediate';

  // Effective row value with pending scalar edits applied. Immediate-mode
  // hosts have no pending layer, so this is a plain read there.
  const getRowValue = useCallback((row, field) => {
    const e = editedRows[row.rowId];
    if (e && Object.prototype.hasOwnProperty.call(e, field)) return e[field];
    return row[field];
  }, [editedRows]);

  const commitCellEdit = useCallback(() => {
    if (!editingCell) return;
    const { rowId, field } = editingCell;
    if (immediate) onCommitEdit?.(rowId, field, editingDraft);
    else setEditedRows((prev) => ({ ...prev, [rowId]: { ...(prev[rowId] || {}), [field]: editingDraft } }));
    setEditingCell(null);
    setEditingDraft('');
  }, [editingCell, editingDraft, immediate, onCommitEdit]);

  const cancelCellEdit = useCallback(() => {
    setEditingCell(null);
    setEditingDraft('');
  }, []);

  // Toggle a pending removal for a row's list field. Clicking the same item
  // again undoes the pending remove (batched mode only — immediate mode
  // applies and saves right away, so there is nothing to un-stage).
  const toggleListDeletion = useCallback((rowId, field, value) => {
    if (!rowId || !field || !value) return;
    if (immediate) { onListOp?.(rowId, field, { op: 'remove', value }); return; }
    setPendingListOps((prev) => updateOps(prev, rowId, field, (ops) => {
      const existing = ops.findIndex((o) => o.op === 'remove' && o.value === value);
      if (existing >= 0) ops.splice(existing, 1);
      else ops.push({ op: 'remove', value });
      return ops;
    }));
  }, [immediate, onListOp]);

  // Stage adding a new item to a list field. Adding the same value twice
  // un-stages it (toggle), mirroring toggleListDeletion.
  const addListItem = useCallback((rowId, field, value) => {
    if (!rowId || !field || !value) return;
    if (immediate) { onListOp?.(rowId, field, { op: 'add', value }); return; }
    setPendingListOps((prev) => updateOps(prev, rowId, field, (ops) => {
      const existing = ops.findIndex((o) => o.op === 'add' && o.value === value);
      if (existing >= 0) ops.splice(existing, 1);
      else ops.push({ op: 'add', value });
      return ops;
    }));
  }, [immediate, onListOp]);

  // Stage an in-place edit of an existing list item. Passing a null/empty
  // newValue (or one equal to the original) clears the staged edit.
  const editListItem = useCallback((rowId, field, value, newValue) => {
    if (!rowId || !field || !value) return;
    if (immediate) { onListOp?.(rowId, field, { op: 'edit', value, newValue }); return; }
    setPendingListOps((prev) => updateOps(prev, rowId, field, (ops) => {
      const next = ops.filter((o) => !(o.op === 'edit' && o.value === value));
      if (newValue && newValue !== value) next.push({ op: 'edit', value, newValue });
      return next;
    }));
  }, [immediate, onListOp]);

  const pendingDeletions = useMemo(() => listOpsToDeletions(pendingListOps), [pendingListOps]);

  const pendingDeletionCount = useMemo(() => {
    let n = 0;
    for (const row of Object.values(pendingDeletions)) {
      for (const arr of Object.values(row)) n += (arr?.length || 0);
    }
    return n;
  }, [pendingDeletions]);

  const pendingListOpCount = useMemo(() => {
    let n = 0;
    for (const row of Object.values(pendingListOps)) {
      for (const ops of Object.values(row)) n += (ops?.length || 0);
    }
    return n;
  }, [pendingListOps]);

  const pendingEditCount = useMemo(
    () => Object.values(editedRows).reduce((a, f) => a + Object.keys(f || {}).length, 0),
    [editedRows],
  );

  const pendingRowOpCount = pendingRowOps.length;

  const resetEdits = useCallback(() => {
    setEditedRows({});
    setPendingListOps({});
    setPendingRowOps([]);
    setEditingCell(null);
    setEditingDraft('');
  }, []);

  return {
    editedRows,
    setEditedRows,
    pendingListOps,
    setPendingListOps,
    pendingRowOps,
    setPendingRowOps,
    pendingRowOpCount,
    pendingDeletions,
    editingCell,
    setEditingCell,
    editingDraft,
    setEditingDraft,
    getRowValue,
    commitCellEdit,
    cancelCellEdit,
    toggleListDeletion,
    addListItem,
    editListItem,
    pendingEditCount,
    pendingDeletionCount,
    pendingListOpCount,
    resetEdits,
  };
}
